// TODO: MEOW-3603 Make this handle more Card cases
import { Body, Box, Heading, Image, useTheme } from '@hover/blueprint';
import { iExternalLink } from '@hover/icons';
import React from 'react';

import { Button } from 'src/shared/components';
import type { OptionalTrackingProperties } from 'src/shared/hooks/useTracking';
import { linkPressed } from 'src/shared/telemetry';

import { pickForegroundColor } from '../../helpers/color';
import { useHomeownerAppContext } from '../../hooks/useHomeownerAppContext';

const CARD_PADDING = 500;

// Cards all share logic for text color on background color
// as well as border radius
export const BasicCard = ({
  backgroundColorProp,
  backgroundImage,
  children,
  padding,
  hasShadow,
  sx,
}: {
  backgroundColorProp?: string;
  backgroundImage?: string;
  children: React.ReactNode;
  padding: number;
  hasShadow?: boolean;
  sx?: object;
}) => {
  const { colors } = useTheme();
  const backgroundColor = backgroundColorProp || colors.neutral[0];

  const { color: textColor } = pickForegroundColor(
    backgroundColor,
    colors.neutral[0],
    colors.neutral[900],
  );

  return (
    <Box
      backgroundColor={backgroundColor}
      bgImage={backgroundImage}
      bgSize="cover"
      borderRadius={400}
      color={textColor}
      flexDirection="column"
      height="100%"
      overflow="hidden"
      padding={padding}
      shadow={hasShadow ? 'distance400' : ''}
      sx={sx}
      width="100%"
    >
      {children}
    </Box>
  );
};

export interface MaterialCardProps {
  backgroundColor?: string;
  backgroundImage?: string;
  coverImageUrl?: string;
  coverImageAlt?: string;
  title: string;
  subtitle?: string;
}

export const MaterialCard = ({
  backgroundColor,
  backgroundImage,
  coverImageUrl,
  coverImageAlt,
  title,
  subtitle,
}: MaterialCardProps) => {
  const { colors, space } = useTheme();
  return (
    <BasicCard
      backgroundColorProp={backgroundColor}
      backgroundImage={backgroundImage}
      padding={0}
      sx={{
        aspectRatio: '1/1' || 'auto',
        width: '100%',
        minHeight: '240px',
        position: 'relative',
      }}
    >
      <Box flex="1">
        {coverImageUrl && (
          <Image
            alt={coverImageAlt || title}
            borderColor="neutral.100"
            borderRadius={300}
            borderStyle="solid"
            borderWidth={400}
            height={600}
            ml={CARD_PADDING}
            mt={CARD_PADDING}
            objectFit="cover"
            src={coverImageUrl}
            width="96px"
          />
        )}
      </Box>
      <Box
        background={
          backgroundImage
            ? `linear-gradient(transparent 0, ${colors.neutral[900]}33 ${space[CARD_PADDING]})`
            : ''
        }
        flexDirection="column"
        p={CARD_PADDING}
        pt={700}
        width="100%"
      >
        <Body
          color={backgroundImage ? colors.neutral[0] : 'inherit'}
          isSemiBold
          isTruncated
          mb={0}
          mt="auto"
          size={500}
        >
          {title}
        </Body>
        <Body
          color={backgroundImage ? colors.neutral[0] : 'inherit'}
          isTruncated
          m={0}
          size={300}
        >
          {subtitle}
        </Body>
      </Box>
    </BasicCard>
  );
};

interface SplitCardProps {
  backgroundImage?: string;
  children: React.ReactNode;
  onResponsiveChangeOrientation: boolean;
}
export const SplitCard = ({
  backgroundImage,
  children,
  onResponsiveChangeOrientation,
}: SplitCardProps) => {
  const { colors } = useTheme();
  const bgColor = colors.neutral[0];

  return (
    <BasicCard
      backgroundColorProp={bgColor}
      hasShadow
      padding={0}
      sx={{
        flexDirection: {
          base: 'column',
          tablet: onResponsiveChangeOrientation ? 'row-reverse' : '',
        },
      }}
    >
      <Box
        bgColor="neutral.100"
        bgImage={backgroundImage}
        bgPosition="center"
        bgSize="cover"
        sx={{
          aspectRatio: {
            base: '4/3',
            tablet: onResponsiveChangeOrientation ? 'auto' : '',
          },
        }}
        width={{
          base: '100%',
          tablet: onResponsiveChangeOrientation ? '40%' : '',
        }}
      />
      <Box
        backgroundColor={bgColor}
        display="block"
        padding={CARD_PADDING}
        width={{
          base: '100%',
          tablet: onResponsiveChangeOrientation ? '60%' : '',
        }}
      >
        {children}
      </Box>
    </BasicCard>
  );
};

export interface LinkoutCardProps {
  buttonHref: string;
  backgroundColor: string;
  buttonText: string;
  title: string;
  description: string;
  optionalTrackingProperties: OptionalTrackingProperties;
}
export const LinkoutCard = ({
  backgroundColor,
  buttonText,
  buttonHref,
  description,
  title,
  optionalTrackingProperties,
}: LinkoutCardProps) => {
  const { trackingData } = useHomeownerAppContext();

  const { colors } = useTheme();

  const { mode: colorScheme } = pickForegroundColor(
    backgroundColor,
    colors.neutral[0],
    colors.neutral[900],
  );

  return (
    <BasicCard
      backgroundColorProp={backgroundColor}
      padding={CARD_PADDING}
      sx={{ aspectRatio: '3/4' }}
    >
      <Heading as="p" color="inherit" mb={0} mt="auto" size={600}>
        {title}
      </Heading>
      <Body color="inherit" flexGrow="1" my={10} size={400}>
        {description}
      </Body>
      <Button
        align-self="flex-end"
        as="a"
        colorScheme={colorScheme}
        href={buttonHref}
        iconAfter={iExternalLink}
        includeTrackingImpression
        optionalTrackingProperties={{
          destination_url: buttonHref,
          ...optionalTrackingProperties,
          ...trackingData,
        }}
        target="_blank"
        textDecoration="none"
        track={linkPressed}
        width="min-content"
      >
        {buttonText}
      </Button>
    </BasicCard>
  );
};
